import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { mapResultsApi } from '@/services';

export const mapResultsKeys = {
  all: ['map-results'] as const,
  forStudent: (studentId: string) => ['map-results', 'student', studentId] as const,
  reportStatus: (studentId: string) => ['map-results', 'report-status', studentId] as const,
  pendingReports: () => ['map-results', 'reports', 'pending'] as const,
};

export function useStudentMapResultsQuery(studentId: string | undefined) {
  return useQuery({
    queryKey: mapResultsKeys.forStudent(studentId ?? ''),
    queryFn: () => mapResultsApi.forStudent(studentId!),
    enabled: !!studentId,
  });
}

export function useMapPreviewMutation() {
  return useMutation({
    mutationFn: ({ file, roster }: { file: File; roster?: File | null }) => mapResultsApi.preview(file, roster),
  });
}

export function useMapImportMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ file, roster }: { file: File; roster?: File | null }) => mapResultsApi.import(file, roster),
    // An import can touch any child in the file, so every student's block is now suspect.
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: mapResultsKeys.all }),
  });
}

export function useMapReportStatusQuery(studentId: string | undefined) {
  return useQuery({
    queryKey: mapResultsKeys.reportStatus(studentId ?? ''),
    queryFn: () => mapResultsApi.reportStatus(studentId!),
    enabled: !!studentId,
  });
}

export function useMapReportsPreviewMutation() {
  return useMutation({
    mutationFn: ({ files }: { files: File[] }) => mapResultsApi.previewReports(files),
  });
}

export function useMapReportsUploadMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ files }: { files: File[] }) => mapResultsApi.uploadReports(files),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: mapResultsKeys.all }),
  });
}

export function useMapPendingReportsQuery() {
  return useQuery({
    queryKey: mapResultsKeys.pendingReports(),
    queryFn: () => mapResultsApi.pendingReports(),
  });
}

export function useMapNotifyReportsMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => mapResultsApi.notifyReports(),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: mapResultsKeys.all }),
  });
}
